<template>
	<v-dialog :value="value" content-class="haq-dialog db2-card-settings" max-width="560px" @click:outside="close" @input="$emit('input', $event)">
		<v-card class="haq-dialog-card" v-if="local">
			<v-card-title class="haq-dialog-card-header">
				<h5>Card settings</h5>
				<v-spacer />
				<v-icon class="close-icon" @click="close">mdi-close</v-icon>
			</v-card-title>
			<v-card-text>
				<HaqTextField v-model="local.name" label="Title" hide-details />
				<HaqSelect
					v-if="type === 'kpi'"
					v-model="local.config.variant"
					:items="kpiVariants"
					item-text="text"
					item-value="value"
					label="Variant"
					hide-details
				/>
				<HaqAutocomplete
					v-if="type === 'kpi'"
					v-model="local.config.icon"
					:items="iconItems"
					item-text="text"
					item-value="value"
					label="Icon"
					hide-details
					:filter="iconFilter"
					:menu-props="iconMenuProps"
				>
					<template v-slot:item="{ item }">
						<span class="db2-icon-sel">
							<v-icon class="mr-2" small>{{ iconGlyph(item) }}</v-icon>
							<span>{{ iconLabel(item) }}</span>
						</span>
					</template>
					<template v-slot:selection="{ item }">
						<span class="db2-icon-sel">
							<v-icon class="mr-2" small>{{ iconGlyph(item) }}</v-icon>
							<span>{{ iconLabel(item) }}</span>
						</span>
					</template>
				</HaqAutocomplete>
				<HaqTextField v-if="type === 'kpi'" v-model="local.config.unit" label="Unit" hide-details />
				<HaqSelect
					v-if="type === 'kpi' && local.config.variant !== 'gauge'"
					v-model="axisLabelsMode"
					:items="kpiAxisOptions"
					item-text="text"
					item-value="value"
					label="Y-axis labels"
					hide-details
				/>
				<HaqSelect
					v-if="type === 'bar'"
					v-model="local.config.variant"
					:items="barVariants"
					item-text="text"
					item-value="value"
					label="Variant"
					hide-details
				/>
				<HaqSelect
					v-if="type === 'bar'"
					v-model="local.config.orientation"
					:items="orientations"
					item-text="text"
					item-value="value"
					label="Orientation"
					hide-details
				/>
				<HaqSelect
					v-if="type === 'equipment'"
					v-model="local.config.variant"
					:items="equipVariants"
					item-text="text"
					item-value="value"
					label="Variant"
					hide-details
				/>
				<HaqSelect
					v-if="type === 'energy' && energyVariants.length > 1"
					v-model="local.config.variant"
					:items="energyVariants"
					item-text="text"
					item-value="value"
					label="Variant"
					hide-details
				/>
				<HaqSelect
					v-if="type === 'text'"
					v-model="local.config.mode"
					:items="textModes"
					item-text="text"
					item-value="value"
					label="Mode"
					hide-details
				/>
				<HaqTextarea v-if="type === 'text' && local.config.mode !== 'log'" class="db2-note-field" v-model="local.config.body" label="Note" hide-details rows="4" />
				<HaqTextField v-if="type === 'divider'" v-model="local.config.label" label="Label" hide-details />
				<HaqTextField v-if="type === 'zoneMap'" v-model="local.config.title" label="Map title" hide-details />
				<template v-if="type === 'kpi' || type === 'trend'">
					<HaqSelect
						v-model="local.config.siteId"
						:items="sites"
						item-text="name"
						item-value="objectId"
						label="Site (for point binding)"
						clearable
						hide-details
						@change="onSiteChange"
					/>
					<HaqSelect
						v-model="local.config.zoneId"
						:items="zones"
						item-text="name"
						item-value="objectId"
						label="Zone"
						clearable
						hide-details
						:disabled="!local.config.siteId"
						@change="onZoneChange"
					/>
					<HaqSelect
						v-if="type === 'kpi'"
						v-model="local.config.pointId"
						:items="points"
						item-text="name"
						item-value="objectId"
						label="Point (values stay mocked in v1)"
						clearable
						hide-details
						:disabled="!local.config.zoneId"
					/>
				</template>
			</v-card-text>
			<v-card-actions class="mt-auto ml-auto">
				<v-btn class="haq-btn haq-btn-secondary mr-4" @click="close">Cancel</v-btn>
				<v-btn class="haq-btn haq-btn-primary" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import HaqTextField from '@/components/form/HaqTextField.vue';
import HaqTextarea from '@/components/form/HaqTextarea.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';
import { DASHBOARD_ICONS, DASHBOARD_ICON_MENU_PROPS, filterDashboardIcon } from './dashboardModel';
import { HTTPClient } from '../../../assets/js/http';
let client = new HTTPClient();

/**
 * Per-card settings dialog (title, variant, optional real point binding).
 */
export default {
	name: 'CardSettingsDialog',
	components: { HaqTextField, HaqTextarea, HaqSelect, HaqAutocomplete },
	props: {
		value: { type: Boolean, default: false },
		card: { type: Object, default: null },
		sites: { type: Array, default: () => [] }
	},
	data() {
		return {
			local: null,
			zones: [],
			points: [],
			kpiVariants: [
				{ text: 'Sparkline', value: 'sparkline' },
				{ text: 'Gauge', value: 'gauge' },
				{ text: 'Alarm count', value: 'alarm' }
			],
			barVariants: [
				{ text: 'Absolute', value: 'absolute' },
				{ text: 'Relative', value: 'relative' }
			],
			orientations: [
				{ text: 'Vertical', value: 'vertical' },
				{ text: 'Horizontal', value: 'horizontal' }
			],
			equipVariants: [
				{ text: 'Icons', value: 'icons' },
				{ text: 'Tiles', value: 'tiles' }
			],
			iconMenuProps: DASHBOARD_ICON_MENU_PROPS,
			energyVariants: [
				{ text: 'Bars', value: 'bars' }
			],
			kpiAxisOptions: [
				{ text: 'Hidden', value: 'hidden' },
				{ text: 'Min / max', value: 'minmax' }
			],
			textModes: [
				{ text: 'Note', value: 'note' },
				{ text: 'Log', value: 'log' }
			]
		};
	},
	computed: {
		/**
		 * Card type.
		 * @returns {string}
		 */
		type() {
			return (this.local && this.local.type) || '';
		},
		/**
		 * Icon autocomplete items; keep the current glyph even if it is not in the list.
		 * @returns {Array<{ text: string, value: string }>}
		 */
		iconItems() {
			const list = DASHBOARD_ICONS.slice();
			const cur = this.local && this.local.config && this.local.config.icon;
			if (cur && list.indexOf(cur) < 0) list.unshift(cur);
			return list.map(icon => ({
				text: String(icon).replace(/^mdi-/, '').replace(/-/g, ' '),
				value: icon
			}));
		},
		/**
		 * Hidden vs min/max labels on the KPI spark Y axis.
		 * @returns {string}
		 */
		axisLabelsMode: {
			get() {
				return this.local && this.local.config && this.local.config.showAxisLabels ? 'minmax' : 'hidden';
			},
			set(v) {
				if (!this.local || !this.local.config) return;
				this.$set(this.local.config, 'showAxisLabels', v === 'minmax');
			}
		}
	},
	watch: {
		value(open) {
			if (open) this.hydrateLocal();
		},
		card: {
			handler() {
				if (this.value) this.hydrateLocal();
			}
		}
	},
	methods: {
		/**
		 * Deep-clone the card into local so edits do not mutate draft until Save.
		 */
		hydrateLocal() {
			if (!this.card) return;
			const copy = JSON.parse(JSON.stringify(this.card));
			if (!copy.config) copy.config = {};
			if (!copy.uniqueId && this.card.uniqueId) copy.uniqueId = this.card.uniqueId;
			if (copy.type === 'bar' && copy.config) {
				const v = copy.config.variant;
				if (v === 'bars') copy.config.variant = 'absolute';
				if (v === 'ranking' || v === 'tableBars') copy.config.variant = 'relative';
			}
			if (copy.type === 'text' && copy.config) {
				if (!copy.config.mode) copy.config.mode = 'note';
				if (!Array.isArray(copy.config.entries)) copy.config.entries = [];
			}
			if (copy.type === 'kpi' && copy.config && copy.config.showAxisLabels == null) {
				copy.config.showAxisLabels = false;
			}
			this.local = copy;
			if (this.local.config.siteId) this.loadZones(this.local.config.siteId);
			if (this.local.config.zoneId) this.loadPoints(this.local.config.siteId, this.local.config.zoneId);
		},
		/**
		 * Close without saving.
		 */
		close() {
			this.$emit('input', false);
		},
		/**
		 * Icon glyph from a select item (object or string).
		 * @param {string|{ value?: string }} item
		 * @returns {string}
		 */
		iconGlyph(item) {
			if (item && typeof item === 'object') return item.value || '';
			return item || '';
		},
		/**
		 * Readable icon name from a select item (object or string).
		 * @param {string|{ text?: string, value?: string }} item
		 * @returns {string}
		 */
		iconLabel(item) {
			if (item && typeof item === 'object') {
				return item.text || String(item.value || '').replace(/^mdi-/, '').replace(/-/g, ' ');
			}
			return String(item || '').replace(/^mdi-/, '').replace(/-/g, ' ');
		},
		/**
		 * Show the full icon list when the menu opens on a selected value.
		 * @param {string|{ value?: string }} item
		 * @param {string} queryText
		 * @param {string} itemText
		 * @returns {boolean}
		 */
		iconFilter(item, queryText, itemText) {
			const cur = this.local && this.local.config && this.local.config.icon;
			const selected = String(cur || '').replace(/^mdi-/, '').replace(/-/g, ' ');
			return filterDashboardIcon(item, queryText, itemText, selected);
		},
		/**
		 * Emit a plain clone so the parent can replace draft.cards by uniqueId.
		 */
		save() {
			if (!this.local) return;
			if (!this.local.config) this.$set(this.local, 'config', {});
			const next = JSON.parse(JSON.stringify(this.local));
			if (!next.uniqueId && this.card) next.uniqueId = this.card.uniqueId;
			if (!next.uniqueId) return;
			this.$emit('save', next);
			this.close();
		},
		/**
		 * Site changed — reload zones.
		 */
		onSiteChange() {
			this.local.config.zoneId = null;
			this.local.config.pointId = null;
			this.zones = [];
			this.points = [];
			if (this.local.config.siteId) this.loadZones(this.local.config.siteId);
		},
		/**
		 * Zone changed — reload points.
		 */
		onZoneChange() {
			this.local.config.pointId = null;
			this.points = [];
			if (this.local.config.siteId && this.local.config.zoneId) {
				this.loadPoints(this.local.config.siteId, this.local.config.zoneId);
			}
		},
		/**
		 * Load zones for a site.
		 * @param {string} siteId
		 */
		async loadZones(siteId) {
			try {
				const res = await client.robustQuery('/api/zones', { siteId });
				this.zones = res.results || [];
			} catch (e) {
				console.error(e);
				this.zones = [];
			}
		},
		/**
		 * Load points for a zone.
		 * @param {string} siteId
		 * @param {string} zoneId
		 */
		async loadPoints(siteId, zoneId) {
			try {
				const res = await client.robustQuery('/api/points', { siteId, zoneId });
				this.points = (res.results || []).map(p => (p.toJSON ? p.toJSON() : p));
			} catch (e) {
				console.error(e);
				this.points = [];
			}
		}
	}
};
</script>
